import { Book } from '@apis/index'
import { createContext, useContext, useState } from 'react'

interface GenreBooksContextProps {
  genreBooks: Record<string, any[]>
  items: any[]
  loading: boolean
  loadingState: boolean
  currentPages: Record<string, number>
  totalItems: Record<string, number>
  totalPages: Record<string, number>
  fetchBooksForGenres: (categories: any[]) => Promise<void>
  loadMoreBooks: (categoryName: string) => Promise<void>
  isLoadMore: Record<string, boolean>
}

const GenreBooksContext = createContext<GenreBooksContextProps>({
  genreBooks: {},
  items: [],
  loading: true,
  loadingState: true,
  totalItems: {},
  totalPages: {},
  currentPages: {},
  isLoadMore: {},
  fetchBooksForGenres: async () => {},
  loadMoreBooks: async () => {},
})

export const useGenreBooks = () => useContext(GenreBooksContext)

export const GenreBooksProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [genreBooks, setGenreBooks] = useState<Record<string, any[]>>({})
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingState, setLoadingState] = useState(true)
  const [currentPages, setCurrentPages] = useState<Record<string, number>>({})
  const [totalItems, setTotalItems] = useState<Record<string, number>>({})
  const [totalPages, setTotalPages] = useState<Record<string, number>>({})
  const [isLoadMore, setIsLoadMore] = useState<Record<string, boolean>>({})
  const fetchBooksForGenres = async (categories: any[]) => {
    setLoading(true)
    setLoadingState(true)

    const updatedBooks: Record<string, any[]> = {}
    const page: Record<string, number> = {}
    const totalPages: Record<string, number> = {}
    const totalItemsMap: Record<string, number> = {}

    await Promise.all(
      categories.map(async (category) => {
        try {
          const res = await Book.getGenreBooks({
            limit: 6,
            page: 1,
            category: category.name,
          })

          const data = res?.items || []
          updatedBooks[category.name] = data
          page[category.name] = res.page || 1
          totalPages[category.name] = res.totalPages || 1
          totalItemsMap[category.name] = res?.totalItems || 1
        } catch (err) {
          console.error(`Failed to fetch books for category ${category.id}`)
        }
      }),
    )

    setGenreBooks(updatedBooks)
    setItems([...Object.values(updatedBooks).flat()])
    setCurrentPages(page)
    setTotalPages(totalPages)
    setTotalItems(totalItemsMap)
    setLoading(false)
    setLoadingState(false)
  }

  const loadMoreBooks = async (categoryName: string) => {
    if (isLoadMore[categoryName]) return

    setIsLoadMore((prev) => ({ ...prev, [categoryName]: true }))

    const currentPage = currentPages[categoryName] || 1
    const totalPages = totalItems[categoryName] || 1

    if (currentPage >= totalPages) return

    try {
      const nextPage = currentPage + 1
      const res = await Book.getGenreBooks({
        limit: 6,
        page: nextPage,
        category: categoryName,
      })

      const newBooks = res?.items || []
      setGenreBooks((prev) => ({
        ...prev,
        [categoryName]: [...(prev[categoryName] || []), ...newBooks],
      }))
      setItems((prev) => [...prev, ...newBooks])
      setCurrentPages((prev) => ({ ...prev, [categoryName]: nextPage }))
    } catch (err) {
      console.error(`Failed to load more books for category ${categoryName}`)
    }

    setIsLoadMore((prev) => ({ ...prev, [categoryName]: false }))
  }

  return (
    <GenreBooksContext.Provider
      value={{
        genreBooks,
        items,
        loading,
        loadingState,
        totalItems,
        fetchBooksForGenres,
        loadMoreBooks,
        currentPages,
        isLoadMore,
        totalPages,
      }}
    >
      {children}
    </GenreBooksContext.Provider>
  )
}
